import { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ViewInArRoundedIcon from "@mui/icons-material/ViewInArRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { AI_ASSISTANT_BLUE } from "../constants";
import { slideThumb } from "./presentationDraftOps";

function Thumb({ item }) {
  const [failed, setFailed] = useState(false);
  const is3d = Boolean(item.modelUrl) || item.kind === "3d";
  const src = slideThumb({ related_3d: item });
  if (src && !failed) {
    return (
      <Box
        component="img"
        src={src}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
    );
  }
  return is3d ? (
    <ViewInArRoundedIcon sx={{ color: AI_ASSISTANT_BLUE }} />
  ) : (
    <ImageOutlinedIcon sx={{ color: "#059669" }} />
  );
}

Thumb.propTypes = { item: PropTypes.object.isRequired };

/**
 * Search the Microlab library (histology slides + 3D models, English names)
 * and pick results from a horizontally scrollable gallery.
 */
export default function LibraryPicker({ onSearch, actions, filter = "all", placeholder, disabled, autoFocus }) {
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [message, setMessage] = useState("");

  const run = async () => {
    const q = query.trim();
    if (!q) return;
    setBusy(true);
    setMessage("");
    try {
      const found = await onSearch(q);
      const list = (Array.isArray(found) ? found : []).filter((item) => {
        const is3d = Boolean(item.modelUrl) || item.kind === "3d";
        if (filter === "3d") return is3d;
        if (filter === "image") return !is3d;
        return true;
      });
      setResults(list);
      if (!list.length) setMessage("No library matches. Try an English structure name.");
    } catch (err) {
      setMessage(err?.message || "Library search failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={0.75}>
        <TextField
          size="small"
          fullWidth
          autoFocus={autoFocus}
          placeholder={placeholder || "Search cartilage, fibroblast, adipose…"}
          value={query}
          disabled={disabled || busy}
          inputProps={{ "aria-label": "Search the Microlab library" }}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              run();
            }
          }}
        />
        <Button
          size="small"
          variant="outlined"
          disabled={disabled || busy || !query.trim()}
          onClick={run}
          aria-label="Search"
          sx={{ minWidth: 40, flexShrink: 0, color: `${AI_ASSISTANT_BLUE} !important`, borderColor: `${AI_ASSISTANT_BLUE} !important` }}
        >
          {busy ? <CircularProgress size={16} /> : <SearchRoundedIcon fontSize="small" />}
        </Button>
      </Stack>
      {message ? (
        <Typography sx={{ mt: 0.6, fontSize: 11.5, color: "#64748b" }}>{message}</Typography>
      ) : null}
      {results.length ? (
        <Box
          role="list"
          sx={{
            mt: 1,
            display: "flex",
            gap: 1,
            overflowX: "auto",
            pb: 0.75,
            scrollSnapType: "x mandatory",
            "&::-webkit-scrollbar": { height: 6 },
            "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(148,163,184,0.5)", borderRadius: 3 },
          }}
        >
          {results.slice(0, 12).map((item) => {
            const is3d = Boolean(item.modelUrl) || item.kind === "3d";
            return (
              <Box
                role="listitem"
                key={`${item.slide_id || item.title_id || item.label}-${item.modelUrl || item.imageUrl || ""}`}
                sx={{
                  flex: "0 0 128px",
                  scrollSnapAlign: "start",
                  borderRadius: 2,
                  border: "1px solid rgba(148,163,184,0.3)",
                  bgcolor: "background.card",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ height: 76, bgcolor: "#f1f5f9", display: "grid", placeItems: "center", position: "relative" }}>
                  <Thumb item={item} />
                  <Typography
                    sx={{
                      position: "absolute",
                      top: 4,
                      left: 4,
                      px: 0.6,
                      borderRadius: 999,
                      fontSize: 9.5,
                      fontWeight: 800,
                      color: is3d ? "#1e3a8a" : "#065f46",
                      bgcolor: is3d ? "rgba(219,234,254,0.95)" : "rgba(209,250,229,0.95)",
                    }}
                  >
                    {is3d ? "3D" : "SLIDE"}
                  </Typography>
                </Box>
                <Typography
                  title={item.label}
                  sx={{ px: 0.75, pt: 0.5, fontSize: 11.5, fontWeight: 600, color: "ink.strong", lineHeight: 1.25, height: 30, overflow: "hidden" }}
                >
                  {item.label}
                </Typography>
                <Stack sx={{ p: 0.5 }} spacing={0.25}>
                  {actions.map((action) => (
                    <Button
                      key={action.label}
                      size="small"
                      disabled={disabled}
                      onClick={() => action.onPick(item)}
                      sx={{ textTransform: "none", fontWeight: 700, fontSize: 11, py: 0.1, minHeight: 0 }}
                    >
                      {action.label}
                    </Button>
                  ))}
                </Stack>
              </Box>
            );
          })}
        </Box>
      ) : null}
    </Box>
  );
}

LibraryPicker.propTypes = {
  onSearch: PropTypes.func.isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string.isRequired, onPick: PropTypes.func.isRequired })
  ).isRequired,
  filter: PropTypes.oneOf(["all", "3d", "image"]),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
};
